import React, { useMemo, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Modal, Alert } from "react-bootstrap";
import { Select, DatePicker, Spin } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import { useCanManageEmployees, useSuperAdminIdentity } from '@/contexts/SuperAdminIdentityContext';
import {
  useActivateEmployeeMutation,
  useDeleteEmployeeMutation,
  useEnrollEmployeeMutation,
  useListEmployeeCandidatesQuery,
  useListEmployeesQuery,
  useSuspendEmployeeMutation,
} from '@/features/employees/employeesApi';
import {
  activeDepartmentSelectOptions,
  departmentFilterOptions,
  useListEmployeeDepartmentsQuery,
} from '@/features/employees/employeeDepartmentsApi';
import {
  activeRoleSelectOptions,
  roleFilterOptions,
  useListEmployeeRolesQuery,
} from '@/features/employees/employeeRolesApi';
import type { Employee } from '@/features/employees/employeeTypes';
import { extractErrorMessage } from '@/features/employees/extractErrorMessage';
import {
  isCanonicalEmployeeId,
  mergeEmployeeRows,
  splitEmployeeRows,
  sortEmployeeTableRows,
  type EmployeeTableSortColumn,
  type EmployeeTableSortDirection,
} from '@/features/employees/mapEmployeeRow';
import { resolveLifecycleReason } from '@/features/employees/employeeLifecycleFormat';
import styles from "./employees.module.scss";

const EMPTY_NEW_EMPLOYEE = {
  auth0Sub: '',
  firstName: '',
  lastName: '',
  email: '',
  departmentId: '',
  employeeRoleKey: '',
  jobTitle: '',
  manager: '',
  hireDate: '',
};

const DELETE_CONFIRM_PHRASE = "DELETE";

function emailForEmployeeForm(email: string): string {
  const trimmed = (email || "").trim();
  return trimmed === "—" ? "" : trimmed;
}

function prefillNamesFromDisplayName(
  displayName: string | null | undefined,
): { firstName: string; lastName: string } {
  const name = (displayName || "").trim();
  if (!name) {
    return { firstName: "", lastName: "" };
  }
  const space = name.indexOf(" ");
  if (space === -1) {
    return { firstName: name, lastName: "" };
  }
  return {
    firstName: name.slice(0, space),
    lastName: name.slice(space + 1).trim(),
  };
}

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const canManageEmployees = useCanManageEmployees();
  const identity = useSuperAdminIdentity();
  const canManageAccessOverrides = identity?.employeeRoleKey === "super-admin";

  const {
    data: employeesData,
    isLoading: employeesLoading,
    isError: employeesError,
    isFetching: employeesFetching,
  } = useListEmployeesQuery();

  const {
    data: candidatesData,
    isFetching: candidatesFetching,
  } = useListEmployeeCandidatesQuery(undefined, {
    skip: !canManageEmployees,
  });

  const { data: employeeRolesData } = useListEmployeeRolesQuery(undefined, {
    skip: !canManageEmployees,
  });
  const { data: employeeDepartmentsData } = useListEmployeeDepartmentsQuery(undefined, {
    skip: !canManageEmployees,
  });

  const [enrollEmployee, { isLoading: enrollingEmployee }] = useEnrollEmployeeMutation();
  const [activateEmployee, { isLoading: activatingEmployee }] = useActivateEmployeeMutation();
  const [suspendEmployee, { isLoading: suspendingEmployee }] = useSuspendEmployeeMutation();
  const [deleteEmployee, { isLoading: deletingEmployee }] = useDeleteEmployeeMutation();

  const governanceSubmitting =
    enrollingEmployee ||
    activatingEmployee ||
    suspendingEmployee ||
    deletingEmployee;

  // Filter state
  const [filters, setFilters] = useState({
    employeeName: undefined as string | undefined,
    department: undefined as string | undefined,
    role: undefined as string | undefined,
    hireDate: undefined as string | undefined,
    status: undefined as string | undefined,
  });
  // Reset counter for forcing re-render of components
  const [resetCounter, setResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [tableSort, setTableSort] = useState<{
    column: EmployeeTableSortColumn;
    direction: EmployeeTableSortDirection;
  }>({ column: "employeeId", direction: "asc" });
  const [showHiddenRecords, setShowHiddenRecords] = useState(false);
  const [showRemoveEmployeeModal, setShowRemoveEmployeeModal] = useState(false);
  const [showAccessOverrideModal, setShowAccessOverrideModal] = useState(false);
  const [accessOverrideEmployee, setAccessOverrideEmployee] = useState<Employee | null>(null);
  const [employeeToRemove, setEmployeeToRemove] = useState<Employee | null>(null);
  const [removeReason, setRemoveReason] = useState('');
  const [deleteConfirmPhrase, setDeleteConfirmPhrase] = useState('');
  const [deleteConfirmEmployeeId, setDeleteConfirmEmployeeId] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [addEmployeeFromPending, setAddEmployeeFromPending] = useState(false);
  const [newEmployeeData, setNewEmployeeData] = useState({ ...EMPTY_NEW_EMPLOYEE });
  const departmentSelectOptions = useMemo(
    () =>
      activeDepartmentSelectOptions(
        employeeDepartmentsData?.departments ?? [],
        newEmployeeData.departmentId,
      ),
    [employeeDepartmentsData?.departments, newEmployeeData.departmentId],
  );
  const roleSelectOptions = useMemo(
    () =>
      activeRoleSelectOptions(
        employeeRolesData?.roles ?? [],
        newEmployeeData.employeeRoleKey,
      ),
    [employeeRolesData?.roles, newEmployeeData.employeeRoleKey],
  );

  const { primary: primaryEmployees, hidden: hiddenEmployees } = useMemo(() => {
    return splitEmployeeRows(
      employeesData?.employees ?? [],
      canManageEmployees ? candidatesData?.candidates ?? [] : [],
    );
  }, [employeesData, candidatesData, canManageEmployees]);

  const listLoading = employeesLoading || employeesFetching || (canManageEmployees && candidatesFetching);

  const allFilterRows = useMemo(() => {
    return mergeEmployeeRows(
      employeesData?.employees ?? [],
      canManageEmployees ? candidatesData?.candidates ?? [] : [],
    );
  }, [employeesData, candidatesData, canManageEmployees]);

  const candidateOptions = useMemo(
    () =>
      (candidatesData?.candidates ?? []).map((candidate) => {
        const email = (candidate.emailAddress || "").trim();
        const label = candidate.displayName
          ? `${candidate.displayName}${email ? ` (${email})` : ""}`
          : email || candidate.auth0Sub;
        return {
          value: candidate.auth0Sub,
          label,
        };
      }),
    [candidatesData],
  );

  const removeModalMode =
    employeeToRemove?.backendStatus === "suspended" ? "delete" : "suspend";

  const deleteEmployeeFromModalValid = useMemo(() => {
    if (!employeeToRemove || removeModalMode !== "delete") return false;
    return (
      deleteConfirmPhrase === DELETE_CONFIRM_PHRASE &&
      deleteConfirmEmployeeId === employeeToRemove.employeeId &&
      deleteReason.trim().length > 0
    );
  }, [
    deleteConfirmEmployeeId,
    deleteConfirmPhrase,
    deleteReason,
    employeeToRemove,
    removeModalMode,
  ]);

  const filterOptions = useMemo(
    () => ({
      employeeNames: allFilterRows.map(
        (employee) => `${employee.firstName} ${employee.lastName}`,
      ),
      departments: departmentFilterOptions(
        employeeDepartmentsData?.departments ?? [],
        allFilterRows.map((employee) => employee.department),
      ),
      roles: roleFilterOptions(
        employeeRolesData?.roles ?? [],
        allFilterRows.map((employee) => employee.role),
      ),
      status: ['Active', 'Inactive'],
    }),
    [allFilterRows, employeeDepartmentsData?.departments, employeeRolesData?.roles],
  );

  const applyEmployeeFilters = (rows: Employee[]) =>
    rows.filter(employee => (
      (!filters.employeeName || `${employee.firstName} ${employee.lastName}` === filters.employeeName) &&
      (!filters.department || employee.department === filters.department) &&
      (!filters.role || employee.role === filters.role) &&
      (!filters.hireDate || employee.hireDate === filters.hireDate) &&
      (!filters.status ||
        (filters.status === 'Active'
          ? employee.backendStatus === 'active'
          : employee.backendStatus !== 'active'))
    ));

  const filteredEmployees = applyEmployeeFilters(primaryEmployees);
  const filteredHiddenEmployees = applyEmployeeFilters(hiddenEmployees);

  const sortedEmployees = useMemo(
    () => sortEmployeeTableRows(filteredEmployees, tableSort.column, tableSort.direction),
    [filteredEmployees, tableSort],
  );

  const sortedHiddenEmployees = useMemo(
    () => sortEmployeeTableRows(filteredHiddenEmployees, tableSort.column, tableSort.direction),
    [filteredHiddenEmployees, tableSort],
  );

  const handleTableSort = (column: EmployeeTableSortColumn) => {
    setTableSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  };

  const renderSortableHeader = (label: string, column: EmployeeTableSortColumn) => {
    const isActive = tableSort.column === column;
    const indicator = isActive ? (tableSort.direction === "asc" ? " ↑" : " ↓") : "";

    return (
      <button
        type="button"
        className={`${styles.sortableHeader}${isActive ? ` ${styles.sortableHeaderActive}` : ""}`}
        onClick={() => handleTableSort(column)}
      >
        {label}
        {indicator}
      </button>
    );
  };
  
  const totalPages = 1; // Since we only have a few test employees

  const renderDetailNavCell = (content: string, employee: Employee) => {
    if (!isCanonicalEmployeeId(employee.employeeId)) {
      return content;
    }
    return (
      <span
        className={styles.tableDetailNav}
        onClick={() => navigate(`/employees/${employee.employeeId}`)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            navigate(`/employees/${employee.employeeId}`);
          }
        }}
        role="link"
        tabIndex={0}
      >
        {content}
      </span>
    );
  };

  const renderPrimaryEmployeeRow = (employee: Employee) => (
    <tr key={employee.rowKey}>
      <td>{renderDetailNavCell(employee.employeeId, employee)}</td>
      <td>{renderDetailNavCell(`${employee.firstName} ${employee.lastName}`.trim(), employee)}</td>
      <td>{employee.email}</td>
      <td>{employee.department}</td>
      <td>{employee.role}</td>
      <td>{employee.manager}</td>
      <td>{employee.activeDisplay}</td>
      <td>{employee.hireDate}</td>
      <td>
        <div className={styles.actionButtons}>
          {isCanonicalEmployeeId(employee.employeeId) && (
            <Button
              variant="link"
              size="sm"
              className={styles.actionLink}
              onClick={() => handleAction('employee-details', employee)}
              title="Employee Details"
            >
              📋
            </Button>
          )}
          {canManageAccessOverrides &&
            isCanonicalEmployeeId(employee.employeeId) &&
            (employee.backendStatus === "active" ||
              employee.backendStatus === "suspended") && (
            <Button
              variant="link"
              size="sm"
              className={`${styles.actionLink} ${styles.comingSoonAction}`}
              onClick={() => handleAction("access-override", employee)}
              title="Employee Access Override (coming soon)"
            >
              🔐
            </Button>
          )}
          {canManageEmployees &&
            (employee.backendStatus === "active" ||
              employee.backendStatus === "suspended") && (
            <Button
              variant="link"
              size="sm"
              className={`${styles.actionLink} ${styles.deleteLink}`}
              onClick={() => handleAction('delete', employee)}
              title={
                employee.backendStatus === "suspended"
                  ? "Delete Employee"
                  : "Suspend Employee"
              }
            >
              🗑️
            </Button>
          )}
        </div>
      </td>
    </tr>
  );

  const renderHiddenEmployeeRow = (employee: Employee) => {
    const lifecycleReason = resolveLifecycleReason(employee);
    const canOpenDetail = isCanonicalEmployeeId(employee.employeeId);
    const isCandidateOnly = employee.backendStatus === "candidate";
    const isAwaitingActivation = employee.backendStatus === "pending";
    const isDeleted = employee.backendStatus === "deleted";

    return (
      <tr key={employee.rowKey}>
        <td>{canOpenDetail ? renderDetailNavCell(employee.employeeId, employee) : employee.employeeId}</td>
        <td>
          {canOpenDetail
            ? renderDetailNavCell(`${employee.firstName} ${employee.lastName}`.trim(), employee)
            : `${employee.firstName} ${employee.lastName}`.trim()}
        </td>
        <td>{employee.email}</td>
        <td>{employee.department}</td>
        <td>{employee.role}</td>
        <td>{employee.manager}</td>
        <td>{employee.activeDisplay}</td>
        <td>{employee.hireDate}</td>
        <td>
          {lifecycleReason ? (
            <span>{lifecycleReason}</span>
          ) : isCandidateOnly ? (
            <span className="text-muted">Pending Employee</span>
          ) : isAwaitingActivation ? (
            <span className="text-muted">Awaiting Activation</span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td>
          {isCandidateOnly && canManageEmployees ? (
            <div className={styles.actionButtons}>
              <Button
                variant="link"
                size="sm"
                className={styles.actionLink}
                onClick={() => openApprovePendingEmployee(employee)}
                title="Approve Employee"
              >
                ✅
              </Button>
            </div>
          ) : isAwaitingActivation && canManageEmployees ? (
            <div className={styles.actionButtons}>
              <Button
                variant="link"
                size="sm"
                className={styles.actionLink}
                onClick={() => handleActivateEmployee(employee)}
                title="Activate Employee"
                disabled={activatingEmployee}
              >
                🔓
              </Button>
            </div>
          ) : isDeleted && canOpenDetail ? (
            <div className={styles.actionButtons}>
              <Button
                variant="link"
                size="sm"
                className={styles.actionLink}
                onClick={() => handleAction("employee-details", employee)}
                title="Employee Details"
              >
                📋
              </Button>
            </div>
          ) : (
            <span className="text-muted" title="No governance record (EMP-NNN) yet">
              —
            </span>
          )}
        </td>
      </tr>
    );
  };

  const handleOnClickNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleOnClickPrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  // Filter change
  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ 
      employeeName: undefined, 
      department: undefined, 
      role: undefined, 
      hireDate: undefined, 
      status: undefined 
    });
    setResetCounter(prev => prev + 1);
  };

  const handleAction = (action: string, employee: Employee) => {
    switch (action) {
      case 'employee-details':
        if (isCanonicalEmployeeId(employee.employeeId)) {
          navigate(`/employees/${employee.employeeId}`);
        }
        break;
      case 'access-override':
        setAccessOverrideEmployee(employee);
        setShowAccessOverrideModal(true);
        break;
      case 'delete':
        setEmployeeToRemove(employee);
        setRemoveReason('');
        setDeleteConfirmPhrase('');
        setDeleteConfirmEmployeeId('');
        setDeleteReason('');
        setShowRemoveEmployeeModal(true);
        break;
      default:
        break;
    }
  };

  const closeRemoveEmployeeModal = () => {
    setShowRemoveEmployeeModal(false);
    setEmployeeToRemove(null);
    setRemoveReason('');
    setDeleteConfirmPhrase('');
    setDeleteConfirmEmployeeId('');
    setDeleteReason('');
  };

  const handleGovernanceAction = async () => {
    if (!employeeToRemove || !removeReason.trim()) {
      return;
    }

    if (employeeToRemove.backendStatus !== "active") {
      toast.error("Only active employees can be suspended from this action.");
      return;
    }

    const reason = removeReason.trim();
    const auth0Sub = employeeToRemove.auth0Sub;
    if (!auth0Sub) {
      toast.error("Employee auth0Sub is missing.");
      return;
    }

    try {
      await suspendEmployee({ auth0Sub, reason }).unwrap();
      toast.success(
        `Suspended ${employeeToRemove.firstName} ${employeeToRemove.lastName}`.trim(),
      );
      closeRemoveEmployeeModal();
    } catch (err: unknown) {
      const msg = extractErrorMessage(err);
      if (msg) toast.error(msg);
    }
  };

  const handleDeleteEmployeeFromModal = async () => {
    if (!employeeToRemove || !deleteEmployeeFromModalValid) {
      return;
    }

    try {
      await deleteEmployee({
        employeeId: employeeToRemove.employeeId,
        confirmPhrase: DELETE_CONFIRM_PHRASE,
        confirmEmployeeId: deleteConfirmEmployeeId.trim(),
        reason: deleteReason.trim(),
      }).unwrap();
      toast.success(`Employee ${employeeToRemove.employeeId} marked as deleted.`);
      closeRemoveEmployeeModal();
    } catch (err: unknown) {
      const msg = extractErrorMessage(err);
      if (msg) toast.error(msg);
    }
  };

  const resetNewEmployeeForm = () => {
    setNewEmployeeData({ ...EMPTY_NEW_EMPLOYEE });
  };

  const closeAddEmployeeModal = () => {
    setShowAddEmployeeModal(false);
    setAddEmployeeFromPending(false);
    resetNewEmployeeForm();
  };

  const openAddEmployeeModal = () => {
    resetNewEmployeeForm();
    setAddEmployeeFromPending(false);
    setShowAddEmployeeModal(true);
  };

  const openApprovePendingEmployee = (employee: Employee) => {
    if (!employee.auth0Sub) {
      toast.error("Pending employee is missing Auth0 identity.");
      return;
    }
    setNewEmployeeData({
      ...EMPTY_NEW_EMPLOYEE,
      auth0Sub: employee.auth0Sub,
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: emailForEmployeeForm(employee.email),
    });
    setAddEmployeeFromPending(true);
    setShowAddEmployeeModal(true);
  };

  const handleActivateEmployee = async (employee: Employee) => {
    if (!employee.auth0Sub) {
      toast.error("Employee auth0Sub is missing.");
      return;
    }

    try {
      await activateEmployee(employee.auth0Sub).unwrap();
      toast.success(
        `Activated ${employee.firstName} ${employee.lastName}`.trim(),
      );
    } catch (err: unknown) {
      const msg = extractErrorMessage(err);
      if (msg) toast.error(msg);
    }
  };

  const handleCandidateSelect = (auth0Sub: string | undefined) => {
    if (!auth0Sub) {
      setNewEmployeeData((prev) => ({
        ...prev,
        auth0Sub: "",
        email: "",
        firstName: "",
        lastName: "",
      }));
      return;
    }

    const candidate = (candidatesData?.candidates ?? []).find(
      (row) => row.auth0Sub === auth0Sub,
    );
    if (!candidate) return;

    const email = (candidate.emailAddress || "").trim();
    const { firstName, lastName } = prefillNamesFromDisplayName(candidate.displayName);
    setNewEmployeeData((prev) => ({
      ...prev,
      auth0Sub: candidate.auth0Sub,
      email,
      firstName,
      lastName,
    }));
  };

  const handleAddEmployee = async () => {
    const {
      auth0Sub,
      email,
      firstName,
      lastName,
      employeeRoleKey,
      jobTitle,
      departmentId,
      manager,
      hireDate,
    } = newEmployeeData;

    if (!email.trim() || !firstName.trim() || !lastName.trim() || !employeeRoleKey) {
      toast.error("Enter first name, last name, email, and role before adding.");
      return;
    }

    try {
      const result = await enrollEmployee({
        emailAddress: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        employeeRoleKey,
        jobTitle: jobTitle.trim() || undefined,
        auth0Sub: auth0Sub.trim() || undefined,
        departmentId: departmentId.trim() || undefined,
        manager: manager.trim() || undefined,
        hireDate: hireDate.trim() || undefined,
      }).unwrap();

      const outcome = result.outcome || "updated";
      const approvedPending = addEmployeeFromPending;
      if (outcome === "created") {
        toast.success(
          result.passwordEmailSent
            ? approvedPending
              ? "Pending employee approved; password setup email sent."
              : "Employee added and password setup email sent."
            : approvedPending
              ? "Pending employee approved and enrolled."
              : "Employee added.",
        );
      } else if (outcome === "restored") {
        toast.success(
          approvedPending ? "Pending employee approved and restored." : "Employee restored and updated.",
        );
      } else if (outcome === "updated" || outcome === "unchanged") {
        toast.success(
          approvedPending ? "Pending employee approved and updated." : "Employee record updated.",
        );
      } else {
        toast.success(
          approvedPending ? "Pending employee approved and activated." : "Employee added and activated.",
        );
      }
      closeAddEmployeeModal();
    } catch (err: unknown) {
      const msg = extractErrorMessage(err);
      if (msg) toast.error(msg);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setNewEmployeeData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Employees" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form className={styles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Employee Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`employeeName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any employee name"
                            value={filters.employeeName}
                            onChange={value => handleFilterChange('employeeName', value)}
                            options={filterOptions.employeeNames.map(name => ({ label: name, value: name }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Department"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`department-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any department"
                            value={filters.department}
                            onChange={value => handleFilterChange('department', value)}
                            options={filterOptions.departments.map(dept => ({ label: dept, value: dept }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Role"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`role-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any role"
                            value={filters.role}
                            onChange={value => handleFilterChange('role', value)}
                            options={filterOptions.roles.map(role => ({ label: role, value: role }))}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Hire Date"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`hireDate-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.hireDateField}`}
                            placeholder="Any hire date"
                            value={filters.hireDate ? new Date(filters.hireDate) : undefined}
                            onChange={date => handleFilterChange('hireDate', date ? date.toISOString().split('T')[0] : undefined)}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status.map(status => ({ label: status, value: status }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4} className="d-flex align-items-center mt-2 mt-md-0">
                        {canManageEmployees && (
                        <Button
                          className="w-100"
                          style={{
                            background: 'white',
                            color: '#e21e26',
                            border: '1px solid #e21e26',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px'
                          }}
                          onClick={openAddEmployeeModal}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add Employee
                        </Button>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
          
          {/* Employees Section */}
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>KrossKast Employees ({filteredEmployees.length})</h3>
            </div>
            
            {/* Employees Table */}
            <Spin spinning={listLoading}>
            <Table hover responsive className="legacy-row-height-table">
              <thead>
                <tr>
                  <th>{renderSortableHeader("Employee ID", "employeeId")}</th>
                  <th>{renderSortableHeader("Name", "name")}</th>
                  <th>{renderSortableHeader("Email", "email")}</th>
                  <th>{renderSortableHeader("Department", "department")}</th>
                  <th>{renderSortableHeader("Role", "role")}</th>
                  <th>{renderSortableHeader("Manager", "manager")}</th>
                  <th>{renderSortableHeader("Active", "active")}</th>
                  <th>{renderSortableHeader("Hire Date", "hireDate")}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employeesError ? (
                  <tr>
                    <td colSpan={9} className="text-center text-danger">
                      Failed to load employees. Please refresh and try again.
                    </td>
                  </tr>
                ) : (
                sortedEmployees.map((employee) => renderPrimaryEmployeeRow(employee))
                )}
                {!listLoading && !employeesError && sortedEmployees.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center">
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            </Spin>
            
            {totalPages > 1 && (
              <PaginationRow
                page={page}
                onPageChange={(p) => setPage(p)}
                totalPages={typeof totalPages === "number" ? totalPages : undefined}
                onClickPrevious={handleOnClickPrev}
                handleOnClickNext={handleOnClickNext}
                disableLeft={page === 1}
                disableNext={page === totalPages}
              />
            )}
          </div>

          {hiddenEmployees.length > 0 && (
            <div className={styles.sectionContainer}>
              <Button
                variant="link"
                className="p-0 mb-3"
                onClick={() => setShowHiddenRecords((value) => !value)}
              >
                {showHiddenRecords ? "Hide" : "Show"} pending & deleted employee records ({hiddenEmployees.length})
              </Button>

              {showHiddenRecords && (
                <>
                  <p className={`text-muted ${styles.hiddenSectionHelp}`}>
                    <strong>Pending Employees</strong> are Auth0 users in the Super Admin tenant who
                    are not yet enrolled (no EMP-### governance row).{" "}
                    <strong>Awaiting Activation</strong> employees have an EMP-### record but are not
                    yet active. <strong>Deleted Employees</strong> are tombstoned former employees;
                    Notes shows the latest delete reason. Use Employee Details (📋) for deleted rows
                    to view lifecycle context, audit history, and purge. Use{" "}
                    <strong>Approve Employee</strong> (✅) for pending rows and{" "}
                    <strong>Activate Employee</strong> (🔓) for awaiting activation rows.
                  </p>
                  <Table hover responsive className="legacy-row-height-table">
                    <thead>
                      <tr>
                        <th>{renderSortableHeader("Employee ID", "employeeId")}</th>
                        <th>{renderSortableHeader("Name", "name")}</th>
                        <th>{renderSortableHeader("Email", "email")}</th>
                        <th>{renderSortableHeader("Department", "department")}</th>
                        <th>{renderSortableHeader("Role", "role")}</th>
                        <th>{renderSortableHeader("Manager", "manager")}</th>
                        <th>{renderSortableHeader("Active", "active")}</th>
                        <th>{renderSortableHeader("Hire Date", "hireDate")}</th>
                        <th>{renderSortableHeader("Notes", "notes")}</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHiddenEmployees.map((employee) => renderHiddenEmployeeRow(employee))}
                      {filteredHiddenEmployees.length === 0 && (
                        <tr>
                          <td colSpan={10} className="text-center">
                            No hidden records match the current filters
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showAccessOverrideModal}
        onHide={() => {
          setShowAccessOverrideModal(false);
          setAccessOverrideEmployee(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Employee Access Override —{" "}
            {accessOverrideEmployee
              ? `${accessOverrideEmployee.firstName} ${accessOverrideEmployee.lastName}`.trim()
              : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {accessOverrideEmployee && (
            <>
              <Alert variant="info" className="mb-3">
                Employee Role is the default access model. Employee Access Overrides will be used
                only for rare per-person exceptions — not as a second permission system.
              </Alert>
              <p className="mb-2">
                <strong>Employee:</strong>{" "}
                {`${accessOverrideEmployee.firstName} ${accessOverrideEmployee.lastName}`.trim()}
              </p>
              <p className="mb-2">
                <strong>Assigned Role:</strong> {accessOverrideEmployee.role}
              </p>
              <p className="text-muted mb-0">
                This employee inherits permissions from their assigned role. The override editor
                (same layout as Edit Role on Employee Roles) will arrive in a dedicated slice.
                Legacy Auth0 permission strings and the old Access Areas grid are no longer used
                on the Employees page.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAccessOverrideModal(false);
              setAccessOverrideEmployee(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Suspend / Delete Employee Modal */}
      <Modal show={showRemoveEmployeeModal} onHide={closeRemoveEmployeeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {removeModalMode === "delete" ? "Delete Employee" : "Suspend Employee"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {employeeToRemove && (
            <div>
              {removeModalMode === "delete" ? (
                <>
                  <Alert variant="warning">
                    This marks the employee as <strong>Deleted</strong> and moves them to Hidden
                    Employee Records. The governance row is retained (tombstone) with the delete
                    reason. The Auth0 user account and org membership are not changed. The EMP
                    number is never reused.
                  </Alert>
                  <p>
                    Delete{" "}
                    <strong>
                      {employeeToRemove.firstName} {employeeToRemove.lastName}
                    </strong>{" "}
                    ({employeeToRemove.employeeId})?
                  </p>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Type <strong>{DELETE_CONFIRM_PHRASE}</strong> to confirm
                    </Form.Label>
                    <Form.Control
                      value={deleteConfirmPhrase}
                      onChange={(e) => setDeleteConfirmPhrase(e.target.value)}
                      placeholder={DELETE_CONFIRM_PHRASE}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Type employee ID <strong>{employeeToRemove.employeeId}</strong> to confirm
                    </Form.Label>
                    <Form.Control
                      value={deleteConfirmEmployeeId}
                      onChange={(e) => setDeleteConfirmEmployeeId(e.target.value)}
                      placeholder={employeeToRemove.employeeId}
                    />
                  </Form.Group>
                  <Form.Group className="mb-0">
                    <Form.Label>Reason (required)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter reason for deleting this employee"
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                    />
                  </Form.Group>
                  <p className="small text-muted mt-3 mb-0">
                    Employee must be suspended. Self-delete and last Super Admin delete are blocked
                    by the API.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Suspend{" "}
                    <strong>
                      {employeeToRemove.firstName} {employeeToRemove.lastName}
                    </strong>
                    ?
                  </p>
                  <Alert variant="warning">
                    <strong>Warning:</strong> This will suspend the employee&apos;s Super Admin
                    access until restored.
                  </Alert>
                  <Form.Group>
                    <Form.Label>Reason (required)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter reason for this action"
                      value={removeReason}
                      onChange={(e) => setRemoveReason(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRemoveEmployeeModal}>
            Cancel
          </Button>
          {removeModalMode === "delete" ? (
            <Button
              variant="danger"
              onClick={handleDeleteEmployeeFromModal}
              disabled={!deleteEmployeeFromModalValid || governanceSubmitting}
            >
              Delete Employee
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={handleGovernanceAction}
              disabled={!removeReason.trim() || governanceSubmitting}
            >
              Suspend Employee
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Add Employee Modal */}
      <Modal show={showAddEmployeeModal} onHide={closeAddEmployeeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {addEmployeeFromPending ? "Approve Pending Employee" : "Add New KrossKast Employee"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addEmployeeFromPending && (
            <p className="text-muted">
              Confirm role and optional profile fields, then approve to create the governance
              record (EMP-###) and activate Super Admin access.
            </p>
          )}
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter first name"
                    value={newEmployeeData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter last name"
                    value={newEmployeeData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email address"
                    value={newEmployeeData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department</Form.Label>
                  <Form.Select
                    value={newEmployeeData.departmentId}
                    onChange={(e) => handleInputChange('departmentId', e.target.value)}
                  >
                    {departmentSelectOptions.map((option) => (
                      <option key={option.value || "empty"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {!addEmployeeFromPending && candidateOptions.length > 0 && (
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Prefill from pending employee (optional)</Form.Label>
                  <div className="customSelectWrapper">
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      className={`customSelect ${styles.selectField}`}
                      placeholder="Select pending employee to prefill"
                      value={newEmployeeData.auth0Sub || undefined}
                      onChange={(value) => handleCandidateSelect(value)}
                      options={candidateOptions}
                      notFoundContent="No matching pending employees"
                      dropdownStyle={{ zIndex: 9999 }}
                      style={{ width: "100%" }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={newEmployeeData.employeeRoleKey}
                    onChange={(e) => handleInputChange('employeeRoleKey', e.target.value)}
                  >
                    <option value="">Select Role</option>
                    {roleSelectOptions.map((option) => (
                      <option key={option.value || "empty"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Title</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter job title (optional)"
                    value={newEmployeeData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Manager</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter manager name (optional)"
                    value={newEmployeeData.manager}
                    onChange={(e) => handleInputChange('manager', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hire Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newEmployeeData.hireDate}
                    onChange={(e) => handleInputChange('hireDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddEmployeeModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddEmployee}
            disabled={
              !newEmployeeData.email.trim() ||
              !newEmployeeData.firstName.trim() ||
              !newEmployeeData.lastName.trim() ||
              !newEmployeeData.employeeRoleKey ||
              governanceSubmitting
            }
          >
            {addEmployeeFromPending ? "Approve Employee" : "Add Employee"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(Employees);
